import { ScenarioGeneratorExt } from '@/classes/scenario/genset/ScenarioGeneratorExt';
import { ScenarioBatteryExt } from '@/classes/scenario/battery/ScenarioBatteryExt';
import { ScenarioLoadExt } from '@/classes/scenario/load/ScenarioLoadExt';
import { ScenarioPVExt } from '@/classes/scenario/pv/ScenarioPVExt';
import { ScenarioGridExt } from '@/classes/scenario/grid/ScenarioGridExt';
import { ScenarioEcoExt } from '@/classes/scenario/eco/ScenarioEcoExt';
import { BusinessModelRef, InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import { Scenario } from '@/dto/scenario/Scenario';
import { ScenarioSizingExt } from '@/classes/scenario/sizing/ScenarioSizingExt';
import { DtoExt } from '@/classes/scenario/DtoExt';
import { ScenarioDevExt } from './dev/ScenarioDevExt';
import { FeedInPricingTypeRef, RevenuesTypeRef } from '@/dto/scenario/eco/EcoReference';
import { EcoRevenuesExt } from '@/classes/scenario/eco/EcoRevenuesExt';
import { FeedInExt } from '@/classes/scenario/eco/FeedInExt';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import { ScenarioRenFr } from '@/dto/scenario/ren-fr/ScenarioRenFr';
import { ScenarioRenFrPVExt } from './ren-fr/pv/ScenarioRenFrPVExt';
import { ScenarioRenFrWindExt } from './ren-fr/wind/ScenarioRenFrWindExt';
import { ScenarioRevenueExt } from './ren-fr/revenue/ScenarioRevenueExt';
import { ScenarioCapexExt } from './ren-fr/capex/ScenarioCapexExt';
import { ScenarioOpexExt } from './ren-fr/opex/ScenarioOpexExt';
import { ScenarioStructureFinanceExt } from './ren-fr/structure-finance/ScenarioStructureFinanceExt';
import { localeDateFromUTC, localeDateToUTC } from '@/helpers/timeHelper';

export class ScenarioExt extends DtoExt {
  public static fromDto(dto: Scenario | ScenarioRenFr): ScenarioExt {
    let scenario: ScenarioExt;
    if (dto.isRenFrance) {
      scenario = new ScenarioExt(ScenarioExt.scenerioRenFrFromDto(dto as ScenarioRenFr));
    } else {
      scenario = new ScenarioExt(ScenarioExt.scenerioFromDto(dto as Scenario));
      if (scenario.pv) {
        scenario.pv.pvCapacity = dto.sizing?.pvReport ?? null;
      }
    }

    return scenario;
  }

  public static scenerioFromDto(dto: Scenario):any {
    return {
      uuid: dto.uuid ?? null,
      name: dto.name,
      totalEnergiesShareholdingInterest: dto.totalEnergiesShareholdingInterest,
      durationYear: dto.durationYear,
      irc: dto.irc,
      mocInflation: dto.mocInflation,
      ircInflation: dto.ircInflation,
      mocFx: dto.mocFx,
      ircFx: dto.ircFx,
      regionalFdThreshold: dto.regionalFdThreshold,
      fx: dto.fx,
      hasBattery: dto.hasBattery,
      hasGenset: dto.hasGenset,
      hasGrid: dto.hasGrid,
      hasInjection: dto.hasInjection,
      injectionModelRef: dto.injectionModelRef,
      businessModelRef: dto.businessModelRef,
      load: dto.load && ScenarioLoadExt.fromDto(dto.load),
      pv: dto.pv && ScenarioPVExt.fromDto(dto.pv),
      grid: dto.grid && ScenarioGridExt.fromDto(dto.grid),
      generator: dto.generator && ScenarioGeneratorExt.fromDto(dto.generator),
      battery: dto.battery && ScenarioBatteryExt.fromDto(dto.battery),
      sizing: dto.sizing && ScenarioSizingExt.fromDto(dto.sizing),
      eco: dto.eco && ScenarioEcoExt.fromDto(dto.eco),
      dev: dto.sizing && ScenarioDevExt.fromDto(dto.sizing),
      isRenFrance: dto.isRenFrance
    };
  }

  public static scenerioRenFrFromDto(dto: ScenarioRenFr): any {
    return {
      uuid: dto.uuid ?? null,
      name: dto.name,
      referenceScenario: dto.referenceScenario,
      analyticalCode: dto.analyticalCode,
      spv: dto.spv,
      projectManager: dto.projectManager,
      startDate: dto.startDate ? localeDateFromUTC(dto.startDate as string) : '',
      constructionDuration: dto.constructionDuration,
      exploitationDuration: dto.exploitationDuration,
      energy: dto.energy,
      technology: dto.technology,
      region: dto.region,
      metropoleRegion: dto.metropoleRegion,
      hasBattery: dto.hasBattery,
      hasGenset: dto.hasGenset,
      hasGrid: dto.hasGrid,
      hasInjection: dto.hasInjection,
      injectionModelRef: dto.injectionModelRef,
      businessModelRef: dto.businessModelRef,
      grid: dto.grid && ScenarioGridExt.fromDto(dto.grid),
      isRenFrance: dto.isRenFrance,
      ren_fr_pv: dto.ren_fr_pv && ScenarioRenFrPVExt.fromDto(dto.ren_fr_pv),
      ren_fr_wind: dto.ren_fr_wind && ScenarioRenFrWindExt.fromDto(dto.ren_fr_wind),
      revenue: dto.revenue && ScenarioRevenueExt.fromDto(dto.revenue),
      capex: dto.capex && ScenarioCapexExt.fromDto(dto.capex),
      opex: dto.opex && ScenarioOpexExt.fromDto(dto.opex),
      structure_finance: dto.structure_finance && ScenarioStructureFinanceExt.fromDto(dto.structure_finance),

      dev: dto.sizing && ScenarioDevExt.fromDto(dto.sizing)
    };
  }

  public project: ProjectMeta | null = null;

  public uuid: string | null = null;

  public isRenFrance: boolean = false;

  public name: string | null = null;
  public totalEnergiesShareholdingInterest: number | null = null;

  public durationYear: number | null = null;
  public irc: string | null = null;

  public mocInflation: string | null = null;
  public ircInflation: string | null = null;
  public mocFx: string | null = null;
  public ircFx: string | null = null;
  public regionalFdThreshold: number | null = null;
  public fx: number | null = null;

  public hasGrid: boolean | null = null;
  public hasBattery: boolean | null = null;
  public hasGenset: boolean | null = null;
  public hasInjection: boolean | null = null;

  public load: ScenarioLoadExt | null = null;
  public generator: ScenarioGeneratorExt | null = null;
  public battery: ScenarioBatteryExt | null = null;
  public pv: ScenarioPVExt | null = null;
  public grid: ScenarioGridExt | null = null;
  public sizing: ScenarioSizingExt | null = null;
  public eco: ScenarioEcoExt | null = null;

  public businessModelRef: BusinessModelRef | null = null; // NOT FED - nothing feeds this field.
  public injectionModelRef: InjectionModelRef | null = null;

  // sandbox field
  public dev: ScenarioDevExt | null = null;

  // Ren fr project
  public referenceScenario: string | null = null;
  public analyticalCode: string | null = null;
  public spv: string | null = null;
  public projectManager: string | null = null;
  public startDate: string | null = '';
  public constructionDuration: number | null = null;
  public exploitationDuration: number | null = null;
  public energy: string | null = null;
  public technology: string | null = null;
  public region: string | null = null;
  public metropoleRegion: string | null = null;
  public ren_fr_pv: ScenarioRenFrPVExt | null = null;
  public ren_fr_wind: ScenarioRenFrWindExt | null = null;
  public revenue: ScenarioRevenueExt | null = null;
  public capex: ScenarioCapexExt | null = null;
  public opex: ScenarioOpexExt | null = null;
  public structure_finance: ScenarioStructureFinanceExt | null = null;

  constructor(options?: Partial<ScenarioExt> | null) {
    super();
    this.customSetter(this, 'injectionModelRef', [
      this.setRevenuesForTotalInjection.bind(this),
      this.setFeedInPricing.bind(this),
      this.updateUploadedPVValue.bind(this),
      this.updateIsRenExplorer.bind(this),
      this.updateIsRenFrance.bind(this)
    ]);
    if (options) {
      DtoExt.assignToThis(this, options);
    }
    if (!this.hasInjection) {
      this.injectionModelRef = null;
    }
  }

  get isRenFranceProject(): boolean {
    return this.isRenFrance || (this.project?.typeRef === ProjectTypeRef.ren_fr_pv_project) || (this.project?.typeRef === ProjectTypeRef.ren_fr_wind_project);
  }

  private updateIsRenExplorer(value: InjectionModelRef | null) {
    const isRenExplorer = (this.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || this.project?.typeRef === ProjectTypeRef.wind_onshore_project) && value === InjectionModelRef.total_injection;
    if (this.pv) {
      this.pv = new ScenarioPVExt({ ...this.pv, isRenExplorer });
    }
  }

  private updateIsRenFrance(value: InjectionModelRef | null) {
    const isRenFrance = (this.project?.typeRef === ProjectTypeRef.ren_fr_pv_project || this.project?.typeRef === ProjectTypeRef.ren_fr_wind_project) && value === InjectionModelRef.total_injection;

    this.isRenFrance = isRenFrance;
  }

  private setRevenuesForTotalInjection(value: InjectionModelRef | null) {
    if (value === InjectionModelRef.total_injection) {
      this.eco = new ScenarioEcoExt({
        ...this.eco,
        revenues: new EcoRevenuesExt({
          ...this.eco?.revenues,
          revenuesTypeRef: this.eco?.revenues.revenuesTypeRef ?? RevenuesTypeRef.savings
        })
      });
    }
  }

  private setFeedInPricing(value: InjectionModelRef | null) {
    if (this.injectionModelRef !== null && value !== InjectionModelRef.total_injection) {
      this.eco = new ScenarioEcoExt({
        ...this.eco,
        feedIn: new FeedInExt({
          ...this.eco?.feedIn,
          feedInPricingTypeRef: FeedInPricingTypeRef.simple_pricing
        })
      });
    }
  }

  private updateUploadedPVValue(value: InjectionModelRef | null) {
    if (this.pv !== null && value !== InjectionModelRef.total_injection) {
      this.pv = new ScenarioPVExt({
        ...this.pv,
        hasUploadedPV: true
      });
    }
  }

  public toDto(): Scenario | ScenarioRenFr {
    let scenario;
    if (this.isRenFranceProject) {
      scenario = new ScenarioRenFr(this.scenerioRenFrToDto());
    } else {

      scenario = new Scenario(this.scenerioToDto());

      if (this.hasInjectionType(InjectionModelRef.total_injection)) {
        scenario.sizing!.pvReport = this.pv?.pvCapacity ?? null;
        if ((this.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || this.project?.typeRef === ProjectTypeRef.wind_onshore_project) && this.pv?.hasSurfaceDefinition) {
          scenario.sizing!.pvReport = this.pv.surfaceDefinition?.pvPlantPower ?? null;
        }
      }
    }
    return scenario;
  }

  private scenerioToDto():any {
    return {
      uuid: this.uuid,
      name: this.name,
      totalEnergiesShareholdingInterest: this.totalEnergiesShareholdingInterest,
      durationYear: this.durationYear,
      irc: this.irc,
      mocInflation: this.mocInflation,
      ircInflation: this.ircInflation,
      mocFx: this.mocFx,
      ircFx: this.ircFx,
      regionalFdThreshold: this.regionalFdThreshold,
      fx: this.fx,
      hasBattery: this.hasBattery,
      hasGenset: this.hasGenset,
      hasGrid: this.hasGrid,
      hasInjection: this.hasInjection,
      injectionModelRef: this.injectionModelRef,
      businessModelRef: this.businessModelRef,
      load: this.load && this.load.toDto(),
      pv: this.pv && this.pv.toDto(),
      grid: this.grid && this.grid.toDto(),
      generator: this.generator && this.generator.toDto(),
      battery: this.battery && this.battery.toDto(),
      eco: this.eco && this.eco.toDto(),
      sizing: this.sizing && this.sizing.toDto(),
      isRenFrance: this.isRenFrance
    };
  }

  private scenerioRenFrToDto():any {
    return {
      uuid: this.uuid,
      name: this.name,
      referenceScenario: this.referenceScenario,
      analyticalCode: this.analyticalCode,
      spv: this.spv,
      projectManager: this.projectManager,
      startDate: localeDateToUTC(new Date(this.startDate as string)),
      constructionDuration: this.constructionDuration,
      exploitationDuration: this.exploitationDuration,
      energy: this.energy,
      technology: this.technology,
      region: this.region,
      metropoleRegion: this.metropoleRegion,
      hasBattery: this.hasBattery,
      hasGenset: this.hasGenset,
      hasGrid: this.hasGrid,
      grid: this.grid && this.grid.toDto(),
      hasInjection: this.hasInjection,
      injectionModelRef: this.injectionModelRef,
      businessModelRef: this.businessModelRef,
      ren_fr_pv: this.ren_fr_pv && this.ren_fr_pv.toDto(),
      ren_fr_wind: this.ren_fr_wind && this.ren_fr_wind.toDto(),
      revenue: this.revenue && this.revenue.toDto(),
      capex: this.capex && this.capex.toDto(),
      opex: this.opex && this.opex.toDto(),
      structure_finance: this.structure_finance && this.structure_finance.toDto(),
      isRenFrance: this.isRenFrance
    };
  }

  public isMultigenset(): boolean {
    return (this.hasGrid === false && this.hasGenset && this.generator?.isMulti) ?? false;
  }

  public clone(): ScenarioExt {
    return ScenarioExt.fromDto(this.toDto());
  }

  public hasInjectionType(injectionType: InjectionModelRef): boolean {
    return (this.hasGrid && this.grid?.hasGrid24 && this.hasInjection === true && this.injectionModelRef === injectionType) ?? false;
  }
}
